import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Reveal } from "@/components/demo/lib/Reveal";
import { useCounter } from "@/components/demo/lib/useCounter";
import { useReveal } from "@/components/demo/lib/useReveal";
import { IMG } from "@/components/demo/lib/images";

const WA = "41765008176";

const SCHEDULE = [
  { time: "06:30", name: "HYROX PREP", coach: "Marco D.", spots: 4, total: 14 },
  { time: "08:00", name: "STRENGTH 101", coach: "Sara V.", spots: 0, total: 12 },
  { time: "12:15", name: "LUNCH BURN", coach: "Luca R.", spots: 9, total: 16 },
  { time: "18:00", name: "OLYMPIC LIFT", coach: "Marco D.", spots: 2, total: 10 },
  { time: "19:15", name: "CONDITIONING", coach: "Elena P.", spots: 7, total: 18 },
  { time: "20:30", name: "MOBILITY & FLOW", coach: "Sara V.", spots: 11, total: 14 },
];

const PLANS = [
  { name: "BASE", price: 99, sub: "/mese", perks: ["Accesso sala 24/7", "App di tracking", "1 corso a settimana", "Spogliatoi & sauna"], cta: "Inizia ora" },
  { name: "ELITE", price: 189, sub: "/mese", perks: ["Tutto del piano BASE", "Corsi illimitati", "Analisi InBody mensile", "Recovery zone & sauna IR"], cta: "Più scelto", featured: true },
  { name: "PRIVATE", price: 449, sub: "/mese", perks: ["Tutto del piano ELITE", "8 sessioni Personal Trainer", "Piano nutrizionale dedicato", "Performance lab annuale"], cta: "Parla con noi" },
];

const FAQ = [
  { q: "Posso provare prima di abbonarmi?", a: "Sì. Ti offriamo 7 giorni di prova gratuita: accesso completo alla sala, un corso a scelta e una valutazione iniziale con un coach. Nessuna carta richiesta." },
  { q: "Ci sono coach realmente certificati?", a: "Tutti i nostri coach hanno certificazione FSA, USA-W, NSCA o equivalente. I profili e le specializzazioni sono pubblici nella sezione dedicata." },
  { q: "Quali sono gli orari di apertura?", a: "I membri accedono in autonomia 24/7 tramite badge. La reception è presidiata da lunedì a venerdì 07:00–22:00 e nei weekend 09:00–19:00." },
  { q: "Posso sospendere l'abbonamento?", a: "Sì, puoi sospendere fino a 60 giorni l'anno per motivi medici, lavorativi o di viaggio, senza costi aggiuntivi." },
];

function Stat({ value, suffix, label }: { value: number; suffix: string; label: string }) {
  const { ref, visible } = useReveal<HTMLDivElement>();
  const v = useCounter(value, visible);
  return (
    <div ref={ref}>
      <div className="font-black text-5xl sm:text-7xl leading-none tracking-tighter text-white tabular-nums">
        {Math.round(v).toLocaleString("it-CH")}
        <span className="text-orange-500">{suffix}</span>
      </div>
      <div className="mt-3 text-[11px] tracking-[0.3em] uppercase text-white/40">{label}</div>
    </div>
  );
}

function ForgeClub() {
  const [occupancy, setOccupancy] = useState(64);
  useEffect(() => {
    const id = setInterval(() => setOccupancy((o) => Math.max(28, Math.min(92, o + (Math.random() * 8 - 4)))), 2400);
    return () => clearInterval(id);
  }, []);
  const [open, setOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const COACHES = [
    { n: "Marco D.", s: "Strength · Olympic Lifting", cert: "FSA L4 · USA-W L2", img: IMG.gym.coach1 },
    { n: "Sara V.", s: "Hyrox · Conditioning", cert: "PT FMH · Hyrox Master", img: IMG.gym.coach2 },
    { n: "Luca R.", s: "Bodybuilding · Riabilitazione", cert: "NSCA CSCS · Functional Training", img: IMG.gym.coach3 },
    { n: "Elena P.", s: "Mobility · Pilates Reformer", cert: "STOTT Certified · FRC Mobility", img: IMG.gym.coach4 },
  ];

  return (
    <div className="min-h-screen bg-black text-white font-sans antialiased selection:bg-orange-500 selection:text-black overflow-x-hidden">
      {/* TICKER */}
      <div className="bg-orange-500 text-black text-[11px] tracking-[0.3em] uppercase font-bold overflow-hidden whitespace-nowrap py-2">
        <div className="inline-block animate-[ticker_28s_linear_infinite] pl-full">
          <span className="px-6">▲ LIVE · {Math.round(occupancy)} ATLETI IN SALA</span>
          <span className="px-6">PROSSIMO CORSO 18:00 · OLYMPIC LIFT</span>
          <span className="px-6">PROVA GRATUITA 7 GIORNI · NESSUNA CARTA RICHIESTA</span>
          <span className="px-6">▲ LIVE · {Math.round(occupancy)} ATLETI IN SALA</span>
          <span className="px-6">NUOVA SEDE LUGANO BREGANZONA · 1.400 m²</span>
        </div>
      </div>

      {/* NAV */}
      <header className="fixed top-7 left-0 right-0 z-50 px-6 sm:px-10">
        <div className="flex items-center justify-between">
          <a href="#" className="font-black text-2xl tracking-tighter">FORGE<span className="text-orange-500">.</span></a>
          <nav className="hidden md:flex items-center gap-9 text-[11px] tracking-[0.3em] uppercase">
            <a href="#plans" className="hover:text-orange-500 transition">Membership</a>
            <a href="#coaches" className="hover:text-orange-500 transition">Coach</a>
            <a href="#schedule" className="hover:text-orange-500 transition">Calendario</a>
            <a href="#faq" className="hover:text-orange-500 transition">FAQ</a>
          </nav>
          <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Vorrei provare Forge Club: 7 giorni gratis")}`} className="bg-orange-500 hover:bg-white text-black px-5 py-3 text-[11px] tracking-[0.25em] uppercase font-bold transition">Prova 7gg</a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex items-end pb-16 pt-32 px-6 sm:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.gym.hero} alt="Atleta Forge in sessione di strength training" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/80" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,black_85%)]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="flex items-center gap-3 mb-8 text-[11px] tracking-[0.3em] uppercase">
            <span className="h-2 w-2 rounded-full bg-orange-500 animate-pulse" /> APERTO ORA · LUGANO BREGANZONA
          </div>
          <h1 className="font-black tracking-tighter leading-[0.82] text-[12vw] sm:text-[11vw] lg:text-[10vw] break-words [overflow-wrap:anywhere]">
            ALLENATI<br />
            <span className="italic font-light tracking-tight">diversamente.</span>
          </h1>
          <div className="mt-12 grid sm:grid-cols-[1fr_auto] gap-8 items-end">
            <p className="text-white/60 max-w-xl text-lg leading-snug">
              Non una palestra qualsiasi. Un private club per chi prende sul serio i propri risultati. Coach FMH certificati, programmazione su misura, 1.400 m² di sala con la migliore attrezzatura europea.
            </p>
            <div className="flex gap-3">
              <a href="#plans" className="bg-orange-500 text-black px-7 py-4 text-xs tracking-[0.3em] uppercase font-bold hover:bg-white transition">Membership</a>
              <a href="#schedule" className="border border-white/30 px-7 py-4 text-xs tracking-[0.3em] uppercase font-bold hover:bg-white hover:text-black transition">Calendario</a>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE OCCUPANCY */}
      <section className="border-y border-white/10 py-10 px-6 sm:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-orange-500 mb-3">▲ Occupazione sala in tempo reale</div>
            <div className="h-3 bg-white/10 overflow-hidden">
              <div style={{ width: `${occupancy}%` }} className="h-full bg-orange-500 transition-[width] duration-700 ease-out" />
            </div>
            <div className="mt-3 flex justify-between text-xs text-white/40 tracking-wider">
              <span>Sala · {Math.round(occupancy)}% della capacità</span>
              <span>Aggiornato adesso</span>
            </div>
          </div>
          <div className="hidden sm:block font-black text-6xl tabular-nums">{Math.round(occupancy)}<span className="text-orange-500">%</span></div>
        </div>
      </section>

      {/* STATS */}
      <section className="px-6 sm:px-10 py-28">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-10">
          <Stat value={1400} suffix=" m²" label="Sala attrezzi" />
          <Stat value={42} suffix="" label="Corsi a settimana" />
          <Stat value={9} suffix="" label="Coach certificati FMH" />
          <Stat value={2300} suffix="+" label="Atleti attivi" />
        </div>
      </section>

      {/* PLANS */}
      <section id="plans" className="px-6 sm:px-10 py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="grid lg:grid-cols-[1fr_2fr] gap-12 mb-20">
              <div className="text-[11px] tracking-[0.3em] uppercase text-orange-500">01 / Membership Premium</div>
              <h2 className="font-black text-5xl sm:text-7xl tracking-tighter leading-[0.9] break-words">
                Tre livelli.<br /><span className="text-white/30">Un solo standard.</span>
              </h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-px bg-white/10">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 80}>
                <div className={`p-8 sm:p-10 h-full flex flex-col ${p.featured ? "bg-orange-500 text-black" : "bg-black"}`}>
                  <div className="flex justify-between items-start mb-12">
                    <div className="font-black text-2xl tracking-tighter">{p.name}</div>
                    {p.featured && <span className="text-[10px] tracking-[0.3em] uppercase font-bold">★ Più scelto</span>}
                  </div>
                  <div className="font-black text-6xl tracking-tighter tabular-nums">CHF {p.price}<span className="text-2xl opacity-50">{p.sub}</span></div>
                  <ul className={`mt-10 space-y-3 text-sm flex-1 ${p.featured ? "" : "text-white/70"}`}>
                    {p.perks.map((perk) => <li key={perk} className="flex gap-3"><span>▸</span>{perk}</li>)}
                  </ul>
                  <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Sono interessato al piano " + p.name)}`} className={`mt-10 block text-center py-4 text-[11px] tracking-[0.3em] uppercase font-bold transition ${p.featured ? "bg-black text-orange-500 hover:bg-white hover:text-black" : "bg-orange-500 text-black hover:bg-white"}`}>{p.cta}</a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SCHEDULE */}
      <section id="schedule" className="px-6 sm:px-10 py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="grid lg:grid-cols-[1fr_2fr] gap-12 mb-16">
              <div className="text-[11px] tracking-[0.3em] uppercase text-orange-500">02 / Calendario di oggi</div>
              <h2 className="font-black text-5xl sm:text-7xl tracking-tighter leading-[0.9] break-words">Allenamenti<br /><span className="italic font-light">ad alte prestazioni.</span></h2>
            </div>
          </Reveal>
          <div className="divide-y divide-white/10">
            {SCHEDULE.map((c, i) => {
              const full = c.spots === 0;
              return (
                <Reveal key={c.time} delay={i * 50}>
                  <div className={`grid grid-cols-[80px_1fr_auto] sm:grid-cols-[120px_1fr_1fr_auto] gap-6 py-6 items-center group hover:bg-white/[0.03] transition ${full ? "opacity-50" : ""}`}>
                    <div className="font-black text-2xl sm:text-3xl tracking-tighter tabular-nums">{c.time}</div>
                    <div>
                      <div className="font-bold tracking-wide">{c.name}</div>
                      <div className="text-xs text-white/40 mt-1">Coach {c.coach}</div>
                    </div>
                    <div className="hidden sm:block text-xs text-white/60 tracking-wider">{full ? "POSTI ESAURITI" : `${c.spots}/${c.total} posti disponibili`}</div>
                    <button disabled={full} className={`text-[10px] tracking-[0.3em] uppercase font-bold px-4 py-2 transition ${full ? "border border-white/10 text-white/30 cursor-not-allowed" : "bg-orange-500 text-black hover:bg-white"}`}>{full ? "Pieno" : "Prenota"}</button>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* COACHES */}
      <section id="coaches" className="px-6 sm:px-10 py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <h2 className="font-black text-5xl sm:text-7xl tracking-tighter leading-[0.9] mb-16 break-words">Personal Trainer<br /><span className="text-white/30">certificati.</span></h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {COACHES.map((c, i) => (
              <Reveal key={c.n} delay={i * 60}>
                <div className="border border-white/10 aspect-[3/4] relative overflow-hidden group hover:border-orange-500 transition">
                  <img src={c.img} alt={`${c.n} — coach ${c.s}`} className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <div className="font-black text-3xl tracking-tighter mb-2">{c.n}</div>
                    <div className="text-xs text-orange-500 tracking-[0.25em] uppercase">{c.s}</div>
                    <div className="text-[10px] text-white/60 mt-3 tracking-wider">{c.cert}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APP DASHBOARD */}
      <section id="app" className="px-6 sm:px-10 py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="text-[11px] tracking-[0.3em] uppercase text-orange-500 mb-6">04 / Progress dashboard</div>
            <h2 className="font-black text-5xl sm:text-6xl tracking-tighter leading-[0.9] mb-8 break-words">Un tracking che<br />ti spinge<br /><span className="italic font-light">oltre il limite.</span></h2>
            <p className="text-white/60 text-lg leading-snug max-w-md">App nativa per iOS e Android: piani di allenamento personalizzati, video tecnici dei coach, log automatico dei tuoi record personali.</p>
            <div className="mt-8 flex gap-3">
              <span className="border border-white/20 px-5 py-3 text-[10px] tracking-[0.3em] uppercase">App Store</span>
              <span className="border border-white/20 px-5 py-3 text-[10px] tracking-[0.3em] uppercase">Google Play</span>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-6 sm:p-8">
              <div className="flex justify-between items-center mb-6 text-[10px] tracking-[0.3em] uppercase text-white/50">
                <span>● Live · Questa settimana</span><span>Coach Marco D.</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-8">
                {[{ l: "Volume", v: "12.4 t" }, { l: "Nuovi PR", v: "+3" }, { l: "Streak", v: "27 gg" }].map((k) => (
                  <div key={k.l} className="bg-white/5 p-4">
                    <div className="font-black text-2xl tracking-tighter">{k.v}</div>
                    <div className="text-[10px] text-white/40 tracking-wider mt-1 uppercase">{k.l}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 h-32 items-end">
                {[40, 65, 30, 80, 55, 95, 70].map((h, i) => (
                  <div key={i} className="flex-1 bg-gradient-to-t from-orange-500 to-orange-500/30" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="flex justify-between mt-3 text-[10px] text-white/40 tracking-wider">
                {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => <span key={i}>{d}</span>)}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="px-6 sm:px-10 py-32 border-t border-white/10">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-[80px] sm:text-[160px] leading-none text-orange-500 font-black -mb-8">"</div>
            <blockquote className="font-black text-3xl sm:text-5xl tracking-tight leading-[1.1] break-words">
              Sei mesi fa pesavo 14 kg in più e non finivo una rampa di scale. Oggi corro la mia prima Hyrox in coppia con mia moglie.
              <span className="text-white/40"> Forge non vende abbonamenti — costruisce atleti.</span>
            </blockquote>
            <div className="mt-12 flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-orange-500 grid place-items-center text-black font-black">D</div>
              <div>
                <div className="font-bold">Davide T., 38 anni · Avvocato</div>
                <div className="text-xs text-white/50 tracking-wider">Membro ELITE · 11 mesi</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="px-6 sm:px-10 py-24 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-[11px] tracking-[0.3em] uppercase text-orange-500 mb-4">FAQ</div>
            <h2 className="font-black text-5xl sm:text-7xl tracking-tighter leading-[0.9] mb-12 break-words">Domande<br /><span className="italic font-light">frequenti.</span></h2>
          </Reveal>
          <div className="space-y-2">
            {FAQ.map((f, i) => (
              <button key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left border border-white/10 hover:border-orange-500/40 transition p-6 block">
                <div className="flex justify-between items-center gap-4">
                  <div className="font-bold text-lg">{f.q}</div>
                  <div className={`text-2xl text-orange-500 transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</div>
                </div>
                <div className={`overflow-hidden transition-all ${openFaq === i ? "max-h-40 mt-4" : "max-h-0"}`}>
                  <p className="text-white/60 leading-relaxed">{f.a}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-10 py-32 border-t border-white/10 bg-orange-500 text-black">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-black text-6xl sm:text-9xl tracking-tighter leading-[0.85] break-words">7 GIORNI.<br /><span className="italic font-light">Zero scuse.</span></h2>
          <p className="mt-8 text-lg max-w-xl mx-auto">Una settimana di prova gratuita: accesso sala, corsi a scelta e valutazione iniziale con un coach. Nessuna carta richiesta.</p>
          <button onClick={() => setOpen(true)} className="mt-10 bg-black text-orange-500 px-10 py-5 text-xs tracking-[0.3em] uppercase font-bold hover:bg-white hover:text-black transition">Attiva la prova gratuita</button>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-12 border-t border-white/10 text-xs text-white/40 tracking-wider">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between gap-6">
          <div>FORGE ATHLETIC CLUB · Via Industria 8, 6900 Lugano · +41 91 220 14 80</div>
          <div>Demo FlashWeb · <Link to="/" className="underline">flash-web.ch</Link></div>
        </div>
      </footer>

      {/* MODAL */}
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm grid place-items-center p-6" onClick={() => setOpen(false)}>
          <div className="bg-black border border-orange-500 max-w-lg w-full p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-6">
              <div className="text-[11px] tracking-[0.3em] uppercase text-orange-500">Prova gratuita 7 giorni</div>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white">✕</button>
            </div>
            <h3 className="font-black text-3xl tracking-tighter mb-6">Attiva ora.</h3>
            <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Vorrei attivare la prova gratuita 7 giorni in Forge Club")}`} className="block text-center bg-orange-500 text-black py-4 text-xs tracking-[0.3em] uppercase font-bold hover:bg-white transition">Continua su WhatsApp</a>
            <p className="text-xs text-white/40 mt-4 text-center">Risposta entro 30 minuti negli orari di apertura.</p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes ticker { 0% { transform: translateX(0) } 100% { transform: translateX(-50%) } }
        .pl-full { padding-left: 100% }
      `}</style>
    </div>
  );
}

export const Route = createFileRoute("/demo/forge-club")({
  head: () => ({
    meta: [
      { title: "Forge Athletic Club — Palestra premium a Lugano (Demo FlashWeb)" },
      { name: "description", content: "Membership premium, personal trainer certificati e allenamenti ad alte prestazioni nel cuore di Lugano. Prova 7 giorni gratis." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: ForgeClub,
});
